import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { TimeRangeInput } from '../../../__generated__/api'

interface Props {
  onChange: (timeRange: TimeRangeInput) => void
  value: TimeRangeInput | null
}

export const displayTimerange = (time?: number) => `${time?.toString().padStart(2, '0')}:00`

const TimerangeSelect: React.FC<Props> = ({ onChange: onChangeProp, value: valueProp }) => {
  const { t } = useTranslation()

  const onChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target

    let start = name === 'start' ? parseInt(value, 10) : valueProp?.start
    let end = name === 'end' ? parseInt(value, 10) : valueProp?.end

    if (start === undefined) {
      start = 0
    }

    if (end === undefined || start > end) {
      end = 24
    }

    onChangeProp({ start, end })
  }

  return (
    <Stack direction="row" spacing={2}>
      <FormControl>
        <InputLabel id="timerange-from-label" shrink>
          {t('component.filterOptions.timeRangeSelect.startLabel')}
        </InputLabel>
        <Select
          value={valueProp?.start?.toString() ?? ''}
          onChange={onChange}
          labelId="timerange-from-label"
          name="start"
          displayEmpty
          renderValue={(value) =>
            value === '' ? t('common.select') : displayTimerange(parseInt(value, 10))
          }
          data-cy="timerange-select-start-select"
        >
          {Array.from({ length: 24 }, (_, i) => (
            <MenuItem key={i} value={i} data-cy={`timerange-select-start-${i}`}>
              {displayTimerange(i)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ paddingTop: '30px' }}>-</Box>
      <FormControl>
        <InputLabel id="timerange-to-label" shrink>
          {t('component.filterOptions.timeRangeSelect.endLabel')}
        </InputLabel>
        <Select
          value={valueProp?.end?.toString() ?? ''}
          onChange={onChange}
          labelId="timerange-to-label"
          name="end"
          displayEmpty
          renderValue={(value) =>
            value === '' ? t('common.select') : displayTimerange(parseInt(value, 10))
          }
          data-cy="timerange-select-end-select"
        >
          {Array.from(
            { length: 24 - (valueProp?.start ?? 0) },
            (_, i) => i + (valueProp?.start ?? 0) + 1
          ).map((i) => (
            <MenuItem key={i} value={i} data-cy={`timerange-select-end-${i}`}>
              {displayTimerange(i)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  )
}

export default TimerangeSelect
